import { useEffect, useId, useMemo, useRef, useState } from "react";

import { CircularProgress } from "@/components/ui/circular-progress";
import { IconButton } from "@/components/ui/icon-button";
import { MUI_AUTOCOMPLETE_PANEL, MUI_MENU_LIST } from "@/components/ui/mui-field-shape";
import {
  MUI_LISTBOX_OPTION_ROW as OPTION_ROW,
  MUI_LISTBOX_PAGE_SIZE,
  muiListboxOptionGround as optionGround,
  nextHighlightIndex,
  scrollRowIntoList,
  type HighlightReason,
} from "@/components/ui/mui-listbox";
import { MuiLink } from "@/components/ui/mui-link";
import { MuiCaretGlyph } from "@/components/ui/mui-select-caret";
import { MuiTypography } from "@/components/ui/mui-typography";
import { OutlinedField } from "@/components/ui/outlined-field";
import { MenuPopoverContent } from "@/components/ui/menu-select";
import { Popover, PopoverAnchor } from "@/components/ui/popover";
import { useCommunitySearch } from "@/hooks/communities";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import {
  communityFieldStatus,
  communityOptionLabel,
  findExactMatch,
  findUnambiguousMatch,
  highlightParts,
  validateCommunityId,
  type CommunityOption,
} from "@/lib/community-id-search";
import { communityDetailPath } from "@/lib/route-paths";
import { cn } from "@/lib/utils";

// The Community ID field — the Communities domain's
// `CommunityIdAutocompleteIsland`
// (`timmy/src/domains/communities/islands/CommunityIdAutocompleteIsland/
// community-id-autocomplete-island.tsx`, cited as ISL), ported at first
// encounter as a shared component because the donor shares it: the spec
// dialogs draw it now, the community create dialogs and the community page
// next (#708). The rules are pure and live in `@/lib/community-id-search`;
// this file is the field around them.
//
// **What it holds, and what it shows.** The value is the matched community's
// **composite id** — what every spec stores — while the input shows its **full
// id**, what JDE and the user call it (`value: compositeId`, `displayValue:
// fullId`, `communitiesApi.ts`). Unmatched, the value is the typed text, as the
// source's form value is.
//
// **Form-library-agnostic.** The donor writes into a react-hook-form instance
// it is handed; this reports instead. `onChange` carries the value, whether it
// is matched, the helper line's message, and the source's three rules — the
// first failing one's message and whether they all pass — so a host's Save rule
// reads `isValid` and compares `compositeId` with the stored one. At submit the
// host hands `ruleMessage` back in as `error`, which is what react-hook-form's
// `fieldState.error` is in the donor: set at submit (the donor's form validates
// on submit and its `setValue` never re-validates), and shown only while
// unmatched.
//
// **The layout** is the island's: a column with a 16px gap (`VStack
// spacing={2}`), the name line **above** the field, and MUI's outlined
// medium `TextField` inside an `Autocomplete` — `disableClearable`, so a popup
// caret and no clear button — with the island's two adornments left of the
// caret.

/** `useDebounce(communityId, 50)` (ISL:78) — the form value, not the keystroke. */
export const COMMUNITY_SEARCH_DEBOUNCE_MS = 50;

/**
 * Past this many options the island pads the last one (ISL:277-287): MUI's
 * popper would not scroll the final row fully into view. Ported with it.
 */
const LAST_OPTION_PAD_THRESHOLD = 14;

interface Highlight {
  index: number;
  reason: HighlightReason;
  /** MUI's `preserveScroll`: a reset that leaves the list where it is. */
  preserveScroll?: boolean;
}

/**
 * No highlight — a **fresh object every time**, because every reset is a DOM
 * sync in MUI (`syncHighlightedIndexToDOM(-1)` puts the list back at its top),
 * even one from "nothing" to "nothing"; a shared constant would compare equal
 * and skip the scroll.
 */
const noHighlight = (preserveScroll = false): Highlight => ({
  index: -1,
  reason: null,
  preserveScroll,
});

/** What MUI's `syncHighlightedIndex` compares a render against. */
interface SyncPoint {
  key: string;
  open: boolean;
  options: readonly CommunityOption[];
  inputValue: string;
  picked: CommunityOption | null;
}

/** Whether `b` opens with `a`'s rows, label for label. */
function startsWithLabels(b: readonly CommunityOption[], a: readonly CommunityOption[]) {
  return (
    a.length <= b.length &&
    a.every((option, index) => communityOptionLabel(option) === communityOptionLabel(b[index]!))
  );
}

function sameLabels(a: readonly CommunityOption[], b: readonly CommunityOption[]) {
  return a.length === b.length && startsWithLabels(b, a);
}

function labelOf(option: CommunityOption | null) {
  return option ? communityOptionLabel(option) : "";
}

export interface CommunityIdChange {
  /** The composite id once matched; the typed text otherwise. */
  compositeId: string;
  matched: boolean;
  /** The helper line's message, or `null` for the blank line. */
  message: string | null;
  /**
   * The first of the source's required, pattern and ambiguity rules that
   * fails, or `null` — what the host hands back as `error` at submit, as
   * react-hook-form's submit put it on `fieldState.error` in the donor.
   */
  ruleMessage: string | null;
  /** Whether those rules all pass. */
  isValid: boolean;
}

export interface CommunityIdAutocompleteProps {
  /**
   * The stored composite id the field opens on. It is resolved through the
   * same search, by exact match only (ISL:87-90,114-125): a stored id was
   * chosen deliberately, so it resolves even where it is ambiguous.
   */
  initialCompositeId?: string;
  onChange?: (change: CommunityIdChange) => void;
  /** The source's field is always required; a host may say otherwise. */
  required?: boolean;
  disabled?: boolean;
  /** The host's submit-time rule message — react-hook-form's `fieldState.error`. */
  error?: string;
  className?: string;
}

/**
 * `@mui/icons-material/CheckCircle` in `success.main` — the react-ui theme's
 * `green[500]` (`theme.ts:39-40`) — at `fontSize='medium'`, 24px.
 *
 * **Announced**, an image named by the island's `aria-label` — a named parity
 * break (#712 D6, owner-ruled 2026-09-29). In the donor MUI's `SvgIcon` hides
 * every icon without a `titleAccess` (`SvgIcon.js:157-158`) and spreads the
 * island's `aria-label` on regardless, so the name is in the DOM and not in
 * the accessibility tree. It appears inside the field's polite live region,
 * so a match is read out as it lands.
 */
function MatchedCheck() {
  return (
    <svg
      data-slot="community-id-check"
      role="img"
      aria-label="found matching JDE Community"
      focusable="false"
      viewBox="0 0 24 24"
      className="text-mui-success size-6 shrink-0 fill-current"
    >
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m-2 15-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8z" />
    </svg>
  );
}

export function CommunityIdAutocomplete({
  initialCompositeId = "",
  onChange,
  required = true,
  disabled = false,
  error,
  className,
}: CommunityIdAutocompleteProps) {
  const [value, setValue] = useState(initialCompositeId);
  const [option, setOption] = useState<CommunityOption | null>(null);
  // MUI's own selected value — set by a pick from the list and by nothing
  // else, since the island leaves `value` uncontrolled (ISL:204-220). It is
  // what `aria-selected` marks, what the highlight syncs to and what a blur
  // resets the input against.
  const [picked, setPicked] = useState<CommunityOption | null>(null);
  // `hasUserEditedRef` (ISL:87-90), as state because the render reads it.
  const [userEdited, setUserEdited] = useState(false);
  // MUI's `isTouchRef` and `touchScrolledRef`: a touch highlights the row it
  // lands on, and a scroll after it makes that highlight stale, so Enter then
  // closes the list rather than picking a row the user scrolled past.
  const isTouch = useRef(false);
  const touchScrolled = useRef(false);
  const [open, setOpenState] = useState(false);
  /** `handleOpen` / `handleClose`: each clears one half of the touch state. */
  function setOpen(next: boolean) {
    if (next === open) return;
    if (next) {
      isTouch.current = false;
    } else {
      touchScrolled.current = false;
      setLatentReason(null);
    }
    setOpenState(next);
  }
  const [focused, setFocused] = useState(false);
  const [highlight, setHighlight] = useState<Highlight>(noHighlight);
  // MUI's `highlightReasonRef` apart from what is painted: the pointer resting
  // on the row already highlighted makes the reason `mouse` **without**
  // repainting it (`handleOptionMouseMove`), and a later keep-by-label sync
  // carries that latent reason. Nothing paints from this.
  const [latentReason, setLatentReason] = useState<HighlightReason>(null);
  /** `setHighlightedIndex`: paints the highlight and records its reason. */
  function highlightTo(next: Highlight) {
    setHighlight(next);
    setLatentReason(next.reason);
  }

  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const firstFocus = useRef(true);
  const inputId = useId();
  const listId = useId();
  const messageId = useId();

  const debounced = useDebouncedValue(value, COMMUNITY_SEARCH_DEBOUNCE_MS);
  const search = useCommunitySearch(debounced);
  const isFetching = search.isFetching;
  const isError = search.isError;

  // **The last answer survives a failed one.** RTK Query's hook hands back the
  // last successful `data` while the current argument errors, so the donor
  // keeps listing (and resolving against) the previous options and shows
  // "Error loading Communities" only when nothing ever loaded. TanStack drops
  // the placeholder on error, so the last success is held here.
  const [lastGood, setLastGood] = useState(search.data);
  if (search.data !== undefined && search.data !== lastGood) setLastGood(search.data);
  const answer = search.data ?? (isError ? lastGood : undefined);
  const options: readonly CommunityOption[] = useMemo(() => answer?.communities ?? [], [answer]);

  const inputValue = option?.externalFullId || value;
  const listed = open && options.length > 0;

  /**
   * `onInputChange` (ISL:92-105): an unambiguous match against the options in
   * hand commits its composite id and shows its full id at once; anything
   * else commits the text as typed and drops the match.
   */
  function handleText(text: string) {
    setUserEdited(true);
    const hit = findUnambiguousMatch(options, text);
    setOption(hit);
    setValue(hit ? hit.externalCompositeId : text);
  }

  /**
   * A pick — MUI's `selectNewValue`, then the island's `onChange`.
   *
   * **Re-picking the option already picked commits it again** — a named parity
   * break (#712 D4, owner-ruled 2026-09-29). In the donor MUI's `handleValue`
   * returns early when the new value *is* the current one (`value ===
   * newValue`), so the island's `onChange` never runs: a user who edits the
   * text after a pick and then picks the same community again is left
   * unmatched on the text they typed. Here every pick commits.
   */
  function pick(next: CommunityOption) {
    setOption(next);
    setPicked(next);
    setValue(next.externalCompositeId);
    setOpen(false);
  }

  /**
   * The island's resolve-on-close (ISL:163-171): while the list is shut and
   * nothing is matched, try the options in hand — the unambiguous rule once
   * the user has typed, the exact rule for a value the field arrived with.
   * This is also what resolves the initial value once its read lands.
   *
   * The donor runs it in a layout effect, so the resolved field is what
   * paints. Here it is state adjusted **during render** — React's own pattern
   * for state that follows other state — which paints nothing stale either,
   * and converges at once: the commit sets `option`, which ends the condition.
   */
  const resolved =
    !open && option === null
      ? userEdited
        ? findUnambiguousMatch(options, value)
        : findExactMatch(options, value)
      : null;
  if (resolved) {
    setOption(resolved);
    setValue(resolved.externalCompositeId);
  }

  /**
   * MUI's `syncHighlightedIndex`, run when the list opens, when the option
   * count, the input or the picked value changes, or when the options' labels
   * do (`filteredOptionsChanged`) — its own triggers. While open: a
   * highlighted option that survived a reload with the input and value
   * unchanged keeps its place (by label); otherwise the **picked option** is
   * highlighted if listed, and nothing is if not. Adjusted during render, as
   * the resolve above is.
   *
   * MUI also re-syncs on the render *after* a relabel, when its
   * `filteredOptionsChanged` flag drops back, and that pass drops a highlight
   * kept by label. It is not emulated: the re-render that fires it in the
   * donor is incidental — the next keystroke or the next fetch — and each of
   * those resets the highlight on its own anyway.
   */
  const labels = options.map(communityOptionLabel).join("\n");
  const syncKey = `${open}|${options.length}|${inputValue}|${picked?.id ?? ""}|${labels}`;
  const [syncPoint, setSyncPoint] = useState<SyncPoint>({
    key: syncKey,
    open,
    options,
    inputValue,
    picked,
  });
  if (syncPoint.key !== syncKey) {
    setSyncPoint({ key: syncKey, open, options, inputValue, picked });
    if (open) {
      // MUI's previous options are the *listed* ones, which are none while
      // the list is shut — so a list that opens as a reload lands keeps
      // nothing by label.
      const previousOptions = syncPoint.open ? syncPoint.options : [];
      const relabelled = !sameLabels(previousOptions, options);
      const sameInputAndValue =
        syncPoint.inputValue === inputValue && labelOf(syncPoint.picked) === labelOf(picked);
      const previous = previousOptions[highlight.index];
      const kept =
        highlight.index !== -1 && previous !== undefined && relabelled && sameInputAndValue
          ? options.findIndex((row) => communityOptionLabel(row) === communityOptionLabel(previous))
          : -1;
      if (kept !== -1) {
        // `setHighlightedIndexFromSync`: the reason is the latent one.
        setHighlight({ index: kept, reason: latentReason, preserveScroll: true });
      } else if (options.length === 0 || picked === null) {
        // Rows appended under an unchanged filter keep the list where it is.
        const appendOnly =
          relabelled &&
          syncPoint.inputValue === inputValue &&
          previousOptions.length > 0 &&
          options.length > previousOptions.length &&
          startsWithLabels(options, previousOptions);
        highlightTo(noHighlight(appendOnly));
      } else {
        highlightTo({ index: options.findIndex((row) => row.id === picked.id), reason: null });
      }
    }
  }

  /**
   * MUI's `syncHighlightedIndexToDOM` scroll: the listbox's own `scrollTop`,
   * never an ancestor's, and **never for the pointer** — only a keyboard or a
   * programmatic highlight brings its option into view. No highlight scrolls
   * the list back to its top.
   */
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    if (highlight.index === -1) {
      if (!highlight.preserveScroll) list.scrollTop = 0;
      return;
    }
    if (highlight.reason === "mouse" || highlight.reason === "touch") return;
    const row = list.querySelector<HTMLElement>(`[data-option-index="${highlight.index}"]`);
    if (row) scrollRowIntoList(list, row);
  }, [highlight]);

  /** The listbox's `onScroll`: a scroll after a touch makes its highlight stale. */
  function handleListScroll() {
    if (isTouch.current) touchScrolled.current = true;
  }

  /** `changeHighlightedIndex` — a no-op while the list is shut, as MUI's is. */
  function moveHighlight(diff: number | "start" | "end") {
    if (!open) return;
    isTouch.current = false;
    touchScrolled.current = false;
    const last = options.length - 1;
    if (last < 0) {
      highlightTo(noHighlight());
      return;
    }
    highlightTo({ index: nextHighlightIndex(highlight.index, diff, last), reason: "keyboard" });
  }

  const status = communityFieldStatus({
    options,
    value,
    matched: option !== null,
    userEdited,
    isFetching,
    isError,
    open,
    error,
  });
  const failedRule = validateCommunityId(value, { ambiguous: status.ambiguous });
  const isValid = failedRule === null || (!required && value === "");
  const ruleMessage = isValid ? null : failedRule;

  // Report the field's state whenever any part of it moves — the mount
  // included, so a host knows where it starts.
  const report = useRef(onChange);
  useEffect(() => {
    report.current = onChange;
  });
  const matched = option !== null;
  useEffect(() => {
    report.current?.({
      compositeId: value,
      matched,
      message: status.message,
      ruleMessage,
      isValid,
    });
  }, [value, matched, status.message, ruleMessage, isValid]);

  return (
    <div
      data-slot="community-id-autocomplete"
      className={cn("flex w-full flex-col items-start gap-4", className)}
    >
      {/* `HStack spacing={1} alignItems='baseline'` (ISL:175-190). The label
          stays when nothing is matched; the name follows it only once one is. */}
      <div className="flex items-baseline gap-2">
        <MuiTypography variant="h5" className="text-lennar-ink-secondary shrink-0">
          Community Name:
        </MuiTypography>
        {option ? (
          // The donor's `Link target='_blank' color='primary' underline='none'`
          // to the community page (ISL:179-188), awake since that page mounted
          // (#679 Q3, #884). A plain anchor, as its host dialog's Based on
          // line is (`edit-spec-name-dialog.tsx`, `BasedOnLine`): a new tab
          // loads the page afresh, so the router has nothing to add.
          <MuiLink
            href={communityDetailPath(option.id, option.externalFullId)}
            target="_blank"
            underline="none"
          >
            {/* An `h5`, as the donor's `Typography variant='h5'` is — so
                heading navigation finds the name as well as its label. */}
            <MuiTypography variant="h5">{option.name}</MuiTypography>
          </MuiLink>
        ) : null}
      </div>

      {/* The Autocomplete's root, which the island names (ISL:205), around the
          TextField's, which carries `aria-live='polite'` (ISL:240) so the
          helper line's changes are announced. */}
      <div className="w-full" aria-label="Community ID Autocomplete">
        <div className="w-full" aria-live="polite">
          <OutlinedField
            label="Community ID"
            required={required}
            shrink={focused || inputValue !== ""}
            error={status.error ? (status.message ?? undefined) : undefined}
            // The island's `' '`: never empty, so the line keeps its height and
            // the field never jumps (ISL:231). MUI draws that blank as an
            // `aria-hidden` zero-width space (`FormHelperText.js`).
            reserveMessageLine
            disabled={disabled}
            htmlFor={inputId}
            // This donor's label is a real `<label for>` (`TextField` hands the
            // input's id to `InputLabel`), so a press on the floated label
            // focuses the field rather than swallowing the click.
            labelActivatesControl
            // MUI's `FormHelperText` is always there, so the input always
            // points at it.
            messageId={messageId}
          >
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverAnchor asChild>
                {/* MUI's `.MuiOutlinedInput-root` at the Autocomplete's medium
                    shape: `padding: 9px`, and 39px on the right for the popup
                    indicator (`26 + 4 + 9`, `Autocomplete.js`). The adornments
                    sit in flow after the input, the caret absolutely. */}
                <div
                  ref={anchorRef}
                  className="relative flex w-full items-center py-[9px] pr-[39px] pl-[9px]"
                  onMouseDown={(event) => {
                    // The root's `handleMouseDown`: a press anywhere in the
                    // control box but the input keeps the focus where it is…
                    if (event.target !== inputRef.current) event.preventDefault();
                    // …and a press on the box itself toggles the list as a
                    // press on the input does (`Autocomplete.js`, the
                    // `inputRoot`'s own `onMouseDown`).
                    if (
                      event.target === event.currentTarget &&
                      event.button === 0 &&
                      !disabled &&
                      (inputValue === "" || !open)
                    ) {
                      setOpen(!open);
                    }
                  }}
                  onClick={(event) => {
                    // MUI's root `handleClick`: a press anywhere in the control
                    // box focuses the input and, on the first press of a visit,
                    // selects its text (`selectOnFocus`).
                    const element = inputRef.current;
                    if (!element || disabled) return;
                    if (event.target !== element) element.focus();
                    if (firstFocus.current && element.selectionEnd === element.selectionStart) {
                      element.select();
                    }
                    firstFocus.current = false;
                  }}
                >
                  <input
                    ref={inputRef}
                    id={inputId}
                    type="text"
                    role="combobox"
                    // `getInputProps`' own attributes (`useAutocomplete.js`).
                    autoComplete="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    aria-autocomplete="list"
                    // The popup's own state, loading and no-options rows
                    // included — a named parity break (#712 D7, owner-ruled
                    // 2026-09-29): MUI reports `listboxAvailable`, open *and*
                    // holding option rows, so modsy's field reads collapsed
                    // over its "Loading…" and "No Communities found" rows.
                    aria-expanded={open}
                    aria-controls={listed ? listId : undefined}
                    aria-activedescendant={
                      listed && highlight.index >= 0 ? `${listId}-${highlight.index}` : undefined
                    }
                    required={required}
                    aria-invalid={status.error || undefined}
                    aria-describedby={messageId}
                    placeholder="Add Community ID"
                    disabled={disabled}
                    value={inputValue}
                    onChange={(event) => {
                      touchScrolled.current = false;
                      handleText(event.target.value);
                      // Typing opens the list; emptying the box does not
                      // (`handleInputChange`).
                      if (event.target.value !== "") setOpen(true);
                    }}
                    onMouseDown={(event) => {
                      if (event.button !== 0 || disabled) return;
                      // `handleInputMouseDown`: a press toggles the list only
                      // while the box is empty or the list is shut.
                      if (inputValue === "" || !open) setOpen(!open);
                    }}
                    onFocus={() => setFocused(true)}
                    onBlur={() => {
                      setFocused(false);
                      firstFocus.current = true;
                      setOpen(false);
                      // `clearOnBlur` resets the input to the picked option's
                      // label, which re-enters the island's `onInputChange`
                      // with the input's **own** text — so nothing is cleared,
                      // and the text is re-resolved as if typed. MUI skips the
                      // reset when the input already reads that label.
                      //
                      // **Only once the user has typed** — a named parity
                      // break (#712 D12, owner-ruled 2026-09-29). In the donor
                      // the re-entry marks even the stored text as typed, so a
                      // stored ambiguous id left before its first search lands
                      // takes the typed rule and stays unmatched. Untyped, the
                      // input holds the stored value, and a blur is no edit.
                      if (userEdited && inputValue !== labelOf(picked)) handleText(inputValue);
                    }}
                    onKeyDown={(event) => {
                      // "Wait until IME is settled" — MUI ignores every key
                      // while a composition is open (`event.which === 229`).
                      if (event.keyCode === 229) return;
                      switch (event.key) {
                        case "ArrowDown":
                        case "ArrowUp":
                          // Moves the highlight only once the list is open:
                          // the first press on a shut list just opens it.
                          event.preventDefault();
                          moveHighlight(event.key === "ArrowDown" ? 1 : -1);
                          setOpen(true);
                          return;
                        case "PageDown":
                        case "PageUp":
                          event.preventDefault();
                          moveHighlight(
                            event.key === "PageDown"
                              ? MUI_LISTBOX_PAGE_SIZE
                              : -MUI_LISTBOX_PAGE_SIZE,
                          );
                          setOpen(true);
                          return;
                        case "Home":
                        case "End":
                          if (!open) return;
                          event.preventDefault();
                          moveHighlight(event.key === "Home" ? "start" : "end");
                          return;
                        case "Enter": {
                          const highlighted = open ? options[highlight.index] : undefined;
                          if (highlighted && !touchScrolled.current) {
                            event.preventDefault();
                            pick(highlighted);
                          } else if (open && touchScrolled.current) {
                            // The highlight is stale from a touch-scroll —
                            // close without picking.
                            event.preventDefault();
                            setOpen(false);
                          }
                          return;
                        }
                        case "Escape":
                          // Only an open list takes the Escape; a shut one lets
                          // it through to the dialog around the field.
                          if (!open) return;
                          event.preventDefault();
                          event.stopPropagation();
                          setOpen(false);
                          return;
                      }
                    }}
                    className={cn(
                      // `padding: 7.5px 4px 7.5px 5px`, the 23px `1.4375em`
                      // line, and the Autocomplete input's `text-overflow:
                      // ellipsis`.
                      "text-lennar-ink min-w-0 flex-1 bg-transparent py-[7.5px] pr-1 pl-[5px] text-base leading-[1.4375em] text-ellipsis outline-none",
                      // MUI's placeholder: the ink at 0.42, and hidden while the
                      // label sits inside the field.
                      "placeholder:text-current placeholder:opacity-[0.42]",
                      !focused && "placeholder:opacity-0",
                      "disabled:cursor-not-allowed",
                    )}
                  />
                  {isFetching && !option ? (
                    <CircularProgress
                      size={20}
                      color="inherit"
                      role="status"
                      aria-live="polite"
                      className="text-lennar-ink shrink-0"
                    />
                  ) : null}
                  {option && !open ? <MatchedCheck /> : null}
                  <IconButton
                    tabIndex={-1}
                    aria-label={open ? "Close" : "Open"}
                    title={open ? "Close" : "Open"}
                    disabled={disabled}
                    onClick={() => setOpen(!open)}
                    // The endAdornment at `right: 9` and `top: calc(50% - 14px)`,
                    // the indicator pulled 2px out (`marginRight: -2`) with its
                    // own `padding: 2` — the 28px box `AutocompleteField` draws.
                    className="absolute top-[calc(50%-14px)] right-[7px] p-0.5"
                  >
                    <MuiCaretGlyph open={open} />
                  </IconButton>
                </div>
              </PopoverAnchor>
              {/* `MenuPopoverContent`, not the vendored panel: inside a modal
                  dialog — the Edit Name & ID dialog is one — a body-portalled
                  list sits outside the dialog's scroll lock and is dead under
                  the wheel and the finger (`dialog-portal-container.ts`). The
                  donor's popper scrolls there, so this one portals into the
                  dialog. */}
              <MenuPopoverContent
                // MUI's popper is presentational; the listbox inside is the
                // widget.
                role="presentation"
                align="start"
                // `CustomPopper`: `placement='bottom'` with `flip` disabled
                // (ISL:314-325) — the list never jumps above the field.
                side="bottom"
                avoidCollisions={false}
                sideOffset={0}
                onOpenAutoFocus={(event) => event.preventDefault()}
                // **Escape is the input's, not the layer's.** Radix dismisses on
                // a capture-phase listener on the document, which closes the
                // list before the input hears the key — so the input could not
                // tell an open list's Escape from a shut one's. The input takes
                // it instead, with MUI's rule (`handleKeyDown`, `Escape`).
                onEscapeKeyDown={(event) => event.preventDefault()}
                onInteractOutside={(event) => {
                  if (anchorRef.current?.contains(event.target as Node)) event.preventDefault();
                }}
                className={cn(
                  MUI_AUTOCOMPLETE_PANEL,
                  // The *listbox* scrolls here, at MUI's 40vh — the paper is
                  // not capped (`AutocompletePaper` is `overflow: auto` only).
                  "max-h-none overflow-visible",
                )}
              >
                {/* MUI's three independent arms (`Autocomplete.js:776-790`):
                    the loading row only over an empty list, the empty row only
                    once settled, and the listbox whenever there are options. */}
                {isFetching && options.length === 0 ? (
                  <p className="text-lennar-ink-secondary px-4 py-3.5 text-base">
                    Loading Communities...
                  </p>
                ) : null}
                {options.length === 0 && !isFetching ? (
                  <p
                    role="presentation"
                    // "Prevent input blur when interacting with the 'no
                    // options' content" — MUI's own words.
                    onMouseDown={(event) => event.preventDefault()}
                    className="text-lennar-ink-secondary px-4 py-3.5 text-base"
                  >
                    {isError ? "Error loading Communities" : "No Communities found"}
                  </p>
                ) : null}
                {options.length > 0 ? (
                  <ul
                    ref={listRef}
                    role="listbox"
                    id={listId}
                    aria-label="Community ID"
                    onMouseDown={(event) => event.preventDefault()}
                    onScroll={handleListScroll}
                    // `AutocompleteListbox`: `8px 0`, capped at 40vh, scrolling,
                    // and `position: relative` so a row's `offsetTop` is
                    // measured from the list.
                    className={cn(
                      "relative m-0 max-h-[40vh] list-none overflow-auto p-0",
                      MUI_MENU_LIST,
                    )}
                  >
                    {options.map((row, index) => {
                      const label = communityOptionLabel(row);
                      const selected = picked?.id === row.id;
                      return (
                        <li
                          key={row.id}
                          id={`${listId}-${index}`}
                          data-option-index={index}
                          role="option"
                          aria-selected={selected}
                          onClick={() => {
                            pick(row);
                            isTouch.current = false;
                          }}
                          // MUI's `onMouseMove`, as it wires it: a mouse — or a
                          // pen, through its compatibility mouse events —
                          // highlights by moving; a finger by landing, below.
                          onMouseMove={() => {
                            if (highlight.index !== index) highlightTo({ index, reason: "mouse" });
                            else setLatentReason("mouse");
                            if (!isTouch.current) touchScrolled.current = false;
                          }}
                          onTouchStart={() => {
                            touchScrolled.current = false;
                            highlightTo({ index, reason: "touch" });
                            isTouch.current = true;
                          }}
                          className={cn(
                            OPTION_ROW,
                            optionGround(selected, index === highlight.index, highlight.reason),
                            options.length > LAST_OPTION_PAD_THRESHOLD &&
                              index === options.length - 1 &&
                              "mb-3",
                          )}
                        >
                          <div>
                            {highlightParts(label, inputValue).map((part, partIndex) => (
                              <span
                                key={partIndex}
                                data-highlight={part.highlight}
                                className={part.highlight ? "font-bold" : "font-normal"}
                              >
                                {part.text}
                              </span>
                            ))}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </MenuPopoverContent>
            </Popover>
          </OutlinedField>
        </div>
      </div>
    </div>
  );
}
